import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  Bell,
  AlertTriangle,
  DollarSign,
  Clock,
  CheckCircle,
  Calendar,
  User,
  FileText,
  TrendingUp,
  Download,
  Search,
  ChevronDown,
  Eye,
  Edit,
  CreditCard,
  Package,
  Receipt
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../utils/axios';
import LoadingSpinner, { TableSkeleton, CardSkeleton, PageLoader } from '../components/LoadingSpinner';
import PaymentModal from '../components/PaymentModal';
import KpiDetailsModal from '../components/KpiDetailsModal';
import { calculatePaymentStatus } from '../utils/paymentUtils';
import InvoiceViewModal from '../components/InvoiceViewModal';
import ClosedOrderViewModal from '../components/ClosedOrderViewModal';
import { generatePDF } from '../utils/pdfGenerator';
import { fetchSettingsFromAPI } from '../utils/settingsApi';

const MultiSelect = ({ label, options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom, // Fixed position relative to viewport
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          (!menuRef.current || !menuRef.current.contains(event.target))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Recalculate on scroll/resize to keep it attached
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const toggleDropdown = () => {
    if (!isOpen) {
      updatePosition(); // Calculate position BEFORE rendering
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const toggleOption = (optionValue) => {
    let newValue;
    if (optionValue === 'all') {
      newValue = ['all'];
    } else {
      const newOptions = value.filter(v => v !== 'all');
      if (newOptions.includes(optionValue)) {
        newValue = newOptions.filter(v => v !== optionValue);
      } else {
        newValue = [...newOptions, optionValue];
      }
      if (newValue.length === 0) newValue = ['all'];
    }
    onChange(newValue);
  };

  const isSelected = (optionValue) => value.includes(optionValue);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={toggleDropdown}
        className="flex items-center justify-between w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent min-w-[200px]"
      >
        <span className="block truncate text-sm">
          {value.includes('all')
            ? `All ${label}`
            : `${value.length} ${label} Selected`}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width
            }}
          >
            {options.map((option) => (
              <div
                key={option.value}
                onClick={() => toggleOption(option.value)}
                className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-50 text-sm"
              >
                <div
                  className={`w-4 h-4 mr-3 border rounded flex items-center justify-center ${isSelected(option.value) ? 'bg-[#0A5F38] border-[#0A5F38]' : 'border-gray-300'}`}
                >
                  {isSelected(option.value) && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <span className={isSelected(option.value) ? 'font-medium text-[#0A5F38]' : 'text-gray-700'}>
                  {option.label}
                </span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

const AdminPayments = () => {
  /* #── ALERTS TAB REMOVED ──────────────────────────────────
  const [activeTab, setActiveTab] = useState('payments');
  #*/
  const [records, setRecords] = useState([]);
  /* #── ALERTS STATE REMOVED ────────────────────────────────
  const [alerts, setAlerts] = useState([]);
  #*/
  const [summary, setSummary] = useState({
    total: 0,
    paid: 0,
    partial: 0,
    pending: 0,
    overdue: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState(['all']);
  const [filterDocType, setFilterDocType] = useState(['all']);
  /* #── ALERTS FILTER STATE REMOVED ─────────────────────────
  const [filterAlertType, setFilterAlertType] = useState(['all']);
  #*/
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [overduePeriod, setOverduePeriod] = useState(30);

  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [kpiModalConfig, setKpiModalConfig] = useState({ isOpen: false, type: null, records: [], title: '' });

  // Document Viewing State
  const [showInvoiceViewModal, setShowInvoiceViewModal] = useState(false);
  const [showClosedOrderViewModal, setShowClosedOrderViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  const location = useLocation();

  useEffect(() => {
    // Check for status query param (supports comma-separated values for multi-select)
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    if (statusParam) {
      setFilterStatus(statusParam.split(','));
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view');

    if (viewId && records.length > 0 && !showInvoiceViewModal && !showClosedOrderViewModal) {
      const recordToView = records.find(r => r.id === viewId || r._id === viewId);
      if (recordToView) {
        handleViewDocument(recordToView);
        // Clean up the URL to prevent reopening on subsequent renders
        window.history.replaceState({}, '', '/admin/payments');
      }
    }
  }, [records, location.search, showInvoiceViewModal, showClosedOrderViewModal]);

  useEffect(() => {
    // Fetch data only (alerts tab removed)
    fetchData();
    
    // Fetch company settings for modals
    const getSettings = async () => {
      try {
        const settings = await fetchSettingsFromAPI();
        setCompanySettings(settings.companyDetails);
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
      }
    };
    getSettings();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      console.log('Fetching payments & alerts data...');
      console.log('Token:', token ? 'Present' : 'Missing');

      const response = await axios.get('/admin/payments-alerts');

      console.log('API Response:', response.data);
      console.log('Records sample:', response.data.records?.[0]);

      setRecords(response.data.records || []);
      setSummary({
        total: response.data.summary?.total || 0,
        paid: response.data.summary?.paid || 0,
        partial: response.data.summary?.partial || 0,
        pending: response.data.summary?.pending || 0,
        overdue: response.data.summary?.overdue || 0,
        totalAmount: response.data.summary?.totalAmount || 0,
        paidAmount: response.data.summary?.paidAmount || 0,
        pendingAmount: response.data.summary?.pendingAmount || 0
      });
      setOverduePeriod(response.data.overduePeriodDays || 30);

      if (!response.data.records || response.data.records.length === 0) {
        toast('No payment records found. This is normal if you have no invoices or closed orders yet.', {
          icon: 'ℹ️',
        });
      }
    } catch (error) {
      console.error('Failed to fetch payments & alerts:', error);
      console.error('Error details:', error.response?.data || error.message);

      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found. Please check backend server.');
      } else {
        toast.error('Failed to load payments and alerts data: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  /* #── ALERTS FETCH FUNCTION REMOVED ──────────────────────────
  const fetchAlerts = async () => {
    try {
      const [alertsResponse, stockAlertsResponse] = await Promise.all([
        axios.get('/admin/alerts').catch(err => ({ data: [] })),
        axios.get('/sales/stock-alerts').catch(err => ({ data: { alerts: [] } }))
      ]);
      const allAlerts = [...(alertsResponse.data || []), ...(stockAlertsResponse.data?.alerts || [])];
      setAlerts(allAlerts);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };
  #*/

  const handleUpdatePayment = (record) => {
    console.log('Update payment for:', record);
    setSelectedRecord(record);
    setShowPaymentModal(true);
  };

  const handleViewDocument = (record) => {
    console.log('Viewing document details:', record);
    setViewingRecord(record.fullRecord);
    if (record.documentType === 'invoice') {
      setShowInvoiceViewModal(true);
    } else {
      setShowClosedOrderViewModal(true);
    }
  };

  const handleDownloadPDF = async (record) => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-download' });
      await generatePDF(record, record.status === 'closed_order' ? 'closed_order' : 'invoice', companySettings);
      toast.success('PDF downloaded!', { id: 'pdf-download' });
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF', { id: 'pdf-download' });
    }
  };

  const handlePaymentSubmit = async (paymentUpdateData) => {
    if (!paymentUpdateData.amount_received || parseFloat(paymentUpdateData.amount_received) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setLoading(true);

    try {
      const endpoint = selectedRecord.documentType === 'invoice'
        ? `/sales/invoices/${selectedRecord.id || selectedRecord._id}/payment`
        : `/sales/closed-orders/${selectedRecord.id || selectedRecord._id}/payment`;

      console.log(`Processing payment for ${selectedRecord.documentType} at ${endpoint}`);

      // Ensure amount is parsed as a number
      const amountReceived = parseFloat(paymentUpdateData.amount_received);

      const response = await axios.put(endpoint, {
        payment_type: paymentUpdateData.payment_type,
        amount_received: amountReceived,
        notes: paymentUpdateData.notes
      });

      // Calculate new values for instant UI update
      const currentPaid = parseFloat(selectedRecord.paidAmount || 0);
      const newPaid = currentPaid + amountReceived;
      const totalAmount = parseFloat(selectedRecord.totalAmount || 0);
      const newStatus = calculatePaymentStatus(newPaid, totalAmount);

      // Update the record in the local state to reflect changes immediately
      const updatedRecords = records.map(r => {
        if (r.id === selectedRecord.id || r._id === selectedRecord._id) {
          return {
            ...r,
            paidAmount: newPaid,
            pendingAmount: Math.max(0, totalAmount - newPaid),
            status: newStatus,
            amountReceived: newPaid // Update other possible field names
          };
        }
        return r;
      });

      setRecords(updatedRecords);
      toast.success('Payment added successfully');
      setShowPaymentModal(false);
      setSelectedRecord(null);

      // Also refresh data from server to ensure full consistency
      fetchData();

    } catch (error) {
      console.error('Failed to add payment:', error);
      toast.error(error.response?.data?.error || 'Failed to add payment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      overdue: { color: 'bg-red-100 text-red-800', label: 'Overdue' },
      partial: { color: 'bg-orange-100 text-orange-800', label: 'Partial' },
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getDocumentTypeIcon = (type) => {
    return type === 'invoice' ? FileText : DollarSign;
  };

  const getDocumentTypeBadge = (type) => {
    return type === 'invoice'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-purple-100 text-purple-800';
  };

  const filteredRecords = records.filter(record => {
    const matchesSearch =
      record.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.documentNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus.includes('all') || filterStatus.includes(record.status);
    const matchesDocType = filterDocType.includes('all') || filterDocType.includes(record.documentType);

    return matchesSearch && matchesStatus && matchesDocType;
  });

  /* #── ALERTS FILTER REMOVED ───────────────────────────────────
  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch =
      alert.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.documentNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterAlertType.includes('all') || filterAlertType.includes(alert.type);
    return matchesSearch && matchesType;
  });
  #*/

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  const exportToCSV = () => {
    const headers = ['Document Type', 'Document Number', 'Customer', 'Total Amount', 'Paid Amount', 'Pending Amount', 'Due Date', 'Status'];

    const csvContent = [
      headers.join(','),
      ...filteredRecords.map(item => [
        item.documentType,
        item.documentNumber,
        item.customerName,
        item.totalAmount,
        item.paidAmount,
        item.pendingAmount,
        formatDate(item.dueDate),
        item.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_alerts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported successfully!');
  };

  // Calculate totals safely
  const totals = {
    total: summary?.pendingAmount || 0,
    overdue: Array.isArray(filteredRecords) ? filteredRecords.filter(r => r?.status === 'overdue').reduce((sum, r) => sum + (r?.pendingAmount || 0), 0) : 0,
    pending: Array.isArray(filteredRecords) ? filteredRecords.filter(r => r?.status === 'pending').reduce((sum, r) => sum + (r?.pendingAmount || 0), 0) : 0
  };

  const handleKpiClick = (type) => {
    let title = '';
    let filtered = [];
    if (type === 'pending') {
      title = 'Total Pending Balance Details';
      filtered = records.filter(r => ['pending', 'partial', 'overdue'].includes(r.status));
    } else if (type === 'overdue') {
      title = 'Overdue Payments';
      filtered = records.filter(r => r.status === 'overdue');
    } else if (type === 'all') {
      title = 'All Payment Records';
      filtered = records;
    }
    setKpiModalConfig({
      isOpen: true,
      type,
      title,
      records: filtered
    });
  };


  if (loading && records.length === 0) {
    return <PageLoader text="Loading payments..." />;
  }

  return (
    <div className="min-h-screen bg-[#F6F8F5] p-6 text-slate-900">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A5F38]">Payments</h1>
        <p className="text-gray-500 mt-1">Monitor financial health and pending transactions</p>
      </div>

      {/* KPI Ribbon */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div 
          onClick={() => handleKpiClick('pending')}
          className="bg-white rounded-xl p-8 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border-t-4 border-[#0A5F38] flex flex-col justify-center items-center text-center cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Total Pending Balance</p>
          <p className="text-4xl font-bold text-slate-800">
            {typeof totals?.total === 'number' ? formatCurrency(totals.total) : '₹0.00'}
          </p>
        </div>
        <div 
          onClick={() => handleKpiClick('overdue')}
          className="bg-white rounded-xl p-8 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border-t-4 border-red-500 flex flex-col justify-center items-center text-center cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Overdue Amount</p>
          <p className={`text-4xl font-bold ${totals?.overdue > 0 ? 'text-red-500' : 'text-green-500'}`}>
            {typeof totals?.overdue === 'number' ? formatCurrency(totals.overdue) : '₹0.00'}
          </p>
        </div>
        <div 
          onClick={() => handleKpiClick('all')}
          className="bg-white rounded-xl p-8 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border-t-4 border-[#C5A57D] flex flex-col justify-center items-center text-center cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Total Records</p>
          <p className="text-4xl font-bold text-gray-700">{summary?.total || 0}</p>
        </div>
      </div>

      {/* #── ALERTS TAB REMOVED ─────────────────────────────────
      Tabs bar with Payments + Alerts tabs has been replaced.
      Only Payments is shown now.
      #── */}

      {/* Unified Filter & Search Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-col md:flex-row items-center gap-4">
        <div className="flex-1 relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by customer name or order number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#0A5F38]/20 focus:border-[#0A5F38] transition-all font-medium placeholder-gray-400 text-slate-800"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto no-scrollbar py-1">
          <div className="min-w-[160px]">
            <MultiSelect
              label="Status"
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'paid', label: 'Paid' },
                { value: 'pending', label: 'Pending' },
                { value: 'overdue', label: 'Overdue' },
                { value: 'partial', label: 'Partial' }
              ]}
              value={filterStatus}
              onChange={setFilterStatus}
            />
          </div>
          <div className="min-w-[160px]">
            <MultiSelect
              label="Types"
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'invoice', label: 'Invoices' },
                { value: 'closedOrder', label: 'Closed Orders' }
              ]}
              value={filterDocType}
              onChange={setFilterDocType}
            />
          </div>
          {/* #── ALERTS FILTER OPTION REMOVED ────────────────────── #*/}

          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-5 py-3 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] transition-colors shadow-lg shadow-[#0A5F38]/20 whitespace-nowrap font-medium"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Content Area - Payments Only */}
      {
        // Payments Table
        filteredRecords.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
            <DollarSign className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium text-lg">No payment records found</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Document Details</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Paid</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Pending</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider pl-8">Due Date</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paginatedRecords.map((record) => (
                      <tr key={record.id} className={`transition-colors group ${record.isDeleted ? 'bg-red-50/60 opacity-75' : 'hover:bg-gray-50'}`}>
                        <td className="px-6 py-4">
                          <button 
                            onClick={() => handleViewDocument(record)}
                            className="flex flex-col text-left hover:opacity-75 transition-opacity"
                          >
                            <span className="font-bold text-[#0A5F38] text-sm hover:underline">
                              {record.documentType === 'invoice'
                                ? (record.documentNumber || record.invoiceNumber || record.invoice_id || record.invoice_number || 'N/A').replace(/-CANCEL-\d+/g, ' (Cancelled)')
                                : (record.documentNumber || record.invoiceNumber || record.invoice_id || record.orderNumber || record.order_number || record.closed_order_number || 'N/A').replace(/-CANCEL-\d+/g, ' (Cancelled)')}
                            </span>
                            <span className="text-xs text-gray-400 font-medium mt-0.5 uppercase tracking-wide">
                              {record.documentType === 'invoice' ? 'Invoice' : 'Closed Order'}
                            </span>
                            {record.isDeleted && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold bg-red-100 text-red-600 rounded uppercase tracking-wide border border-red-200">
                                &#9888; Record Deleted
                              </span>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-bold text-slate-800 text-sm">{record.customerName}</span>
                            <span className="text-xs text-gray-400 mt-0.5">{record.customerPhone || 'No Phone'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-bold text-slate-700 text-sm">{formatCurrency(record.totalAmount)}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-bold text-green-600 text-sm">{formatCurrency(record.paidAmount)}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`font-bold text-sm ${record.pendingAmount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {formatCurrency(record.pendingAmount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 pl-8">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-700">{formatDate(record.dueDate)}</span>
                            {record.isOverdue && (
                              <span className="text-[10px] font-bold text-red-500 uppercase tracking-wide mt-0.5">
                                {record.overdueDays} Days Late
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getStatusBadge(record.status)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {record.isDeleted ? (
                            <span className="text-xs font-bold text-red-500 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg">
                              Deleted — No Edit
                            </span>
                          ) : (
                            <button
                              onClick={() => handleUpdatePayment(record)}
                              className="text-sm font-bold text-[#0A5F38] hover:text-[#084d2d] bg-[#0A5F38]/5 hover:bg-[#0A5F38]/10 px-4 py-2 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                            >
                              Update
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {filteredRecords.length > itemsPerPage && (
                <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-white disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-white disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, filteredRecords.length)}</span> of{' '}
                        <span className="font-medium">{filteredRecords.length}</span> results
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-700">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-50"
                        >
                          <ChevronDown className="h-5 w-5 rotate-90" />
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                              ? 'z-10 bg-[#0A5F38]/10 border-[#C5A57D] text-[#0A5F38]'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-white'
                              }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-50"
                        >
                          <ChevronDown className="h-5 w-5 -rotate-90" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )
      }
      {/* #── ALERTS SECTION REMOVED ─────────────────────────────────
      The entire Alerts tab content (alert cards, alert pagination) has been
      commented out. The page now shows Payments only.
      #── */}
      {/* Payment Modal */}
      {showPaymentModal && selectedRecord && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedRecord(null);
          }}
          record={selectedRecord.fullRecord || selectedRecord}
          onPaymentUpdate={handlePaymentSubmit}
          paymentHistory={selectedRecord.fullRecord?.payments || selectedRecord.payments || []}
        />
      )}

      {/* Invoice View Modal */}
      {showInvoiceViewModal && viewingRecord && (
        <InvoiceViewModal
          show={showInvoiceViewModal}
          onClose={() => {
            setShowInvoiceViewModal(true); // Close current view
            setShowInvoiceViewModal(false);
            setViewingRecord(null);
          }}
          invoice={viewingRecord}
          companySettings={companySettings}
          onDownloadPDF={() => handleDownloadPDF(viewingRecord)}
          onUpdatePayment={(record) => {
            setShowInvoiceViewModal(false);
            setViewingRecord(null);
            handleUpdatePayment({
              ...record,
              id: record._id,
              documentType: 'invoice'
            });
          }}
        />
      )}

      {/* Closed Order View Modal */}
      {showClosedOrderViewModal && viewingRecord && (
        <ClosedOrderViewModal
          show={showClosedOrderViewModal}
          onClose={() => {
            setShowClosedOrderViewModal(false);
            setViewingRecord(null);
          }}
          order={viewingRecord}
          companySettings={companySettings}
          onDownloadPDF={() => handleDownloadPDF(viewingRecord)}
          onAddPayment={(order) => {
            setShowClosedOrderViewModal(false);
            setViewingRecord(null);
            handleUpdatePayment({
              // Spread the raw order document
              ...order,
              // Normalize fields PaymentModal expects
              id: order._id,
              documentType: 'closedOrder',
              documentNumber: order.closed_order_number || order.order_number || order.orderNumber,
              customerName: order.customer_name || order.customerId?.name,
              // Keep fullRecord so PaymentModal can access payments history
              fullRecord: order
            });
          }}
        />
      )}

      <KpiDetailsModal
        isOpen={kpiModalConfig.isOpen}
        onClose={() => setKpiModalConfig({ ...kpiModalConfig, isOpen: false })}
        title={kpiModalConfig.title}
        records={kpiModalConfig.records}
        type={kpiModalConfig.type}
        onViewDocument={(record) => {
          setKpiModalConfig({ ...kpiModalConfig, isOpen: false });
          handleViewDocument(record);
        }}
        onUpdatePayment={(record) => {
          setKpiModalConfig({ ...kpiModalConfig, isOpen: false });
          // Ensure correct object shape depending on record type
          if (record.documentType === 'closedOrder') {
            const orderData = record.fullRecord || record;
            handleUpdatePayment({
              ...record,
              id: orderData._id || record.id,
              documentNumber: orderData.closed_order_number || record.documentNumber,
              customerName: orderData.customer_name || orderData.customerId?.name || record.customerName,
              fullRecord: orderData
            });
          } else {
            handleUpdatePayment(record);
          }
        }}
      />
    </div>
  );
};

export default AdminPayments;



